import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../Admin/constants";

export default function OrderLabelDialog({ order, onClose }) {
  const { t } = useTranslation(["boOrderLabel", "boOrders", "boShared", "common"]);
  const cardRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const open = Boolean(order);
  if (!order) return null;

  const addressLine = [
    order.addressCity,
    order.addressStreet,
    order.addressBuildingNumber ? t("boOrderLabel:bldg", { n: order.addressBuildingNumber }) : null,
    order.addressApartmentNumber ? t("boOrderLabel:apt", { n: order.addressApartmentNumber }) : null,
  ]
    .filter(Boolean)
    .join(", ");

  const qrValue = JSON.stringify({
    orderId: order.orderId,
    orderNumber: order.orderNumber,
  });

  const handleDownload = async () => {
    if (isSaving || !cardRef.current) return;
    try {
      setIsSaving(true);
      setError("");
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#ffffff", scale: 2 });
      const link = document.createElement("a");
      link.download = `order-label-${order.orderNumber}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      setError(err?.message || t("boOrderLabel:downloadError"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {t("boOrderLabel:title")}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          ref={cardRef}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            bgcolor: "#fff",
          }}
        >
          <Typography sx={{ mb: 1.5, fontWeight: 600 }}>{order.orderNumber}</Typography>

          <Box sx={{ p: 1.5, bgcolor: "#fff", borderRadius: 1.5, mb: 2 }}>
            <QRCodeSVG value={qrValue} size={180} />
          </Box>

          <Box sx={{ alignSelf: "stretch" }}>
            <LabelRow label={t("boOrders:customer")} value={order.customerName} />
            {order.customerPhone && <LabelRow label={t("boShared:phone")} value={order.customerPhone} />}
            <LabelRow label={t("boOrderLabel:address")} value={addressLine} />
            {order.addressAdditionalDetails && <LabelRow label={t("boOrders:notes")} value={order.addressAdditionalDetails} />}
          </Box>

          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ alignSelf: "stretch", mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Typography variant="body2">{t("common:total")}</Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatCurrency(order.totalAmount)}
            </Typography>
          </Stack>
        </Box>

        <Button
          fullWidth
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={isSaving}
          sx={{ mt: 2.5 }}
        >
          {isSaving ? t("boShared:saving") : t("boOrderLabel:downloadImage")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function LabelRow({ label, value }) {
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      sx={{ py: 0.75, borderTop: "1px solid", borderColor: "divider" }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ width: 78, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {value}
      </Typography>
    </Stack>
  );
}
